// Two kinds of session, stored deliberately differently:
//
// - Tenant/owner login ("token"/"tenant") lives in localStorage — a
//   normal persistent login, like any SaaS account, that survives
//   closing the browser.
// - Staff PIN sessions ("staff_token"/"staff"/"staff_tenant") live in
//   sessionStorage instead — tied to a shared restaurant device where
//   the PIN identifies who's using it *right now*. Closing that
//   browser tab/window ends the session automatically, rather than
//   leaving a waiter's access sitting there indefinitely (e.g. still
//   usable from home hours after their shift, if the device were ever
//   taken off-site). This is on top of, not instead of, the shift-
//   schedule enforcement the backend does independently.

export function getAuthHeader() {
  const tenantToken = localStorage.getItem("token");
  if (tenantToken) return { Authorization: `Bearer ${tenantToken}` };

  const staffToken = sessionStorage.getItem("staff_token");
  if (staffToken) return { Authorization: `Bearer ${staffToken}` };

  return {};
}

export function getSessionType() {
  if (localStorage.getItem("token")) return "tenant";
  if (sessionStorage.getItem("staff_token")) return "manager";
  return null;
}

export function getLoginRedirect() {
  // Where to send the person if their session is missing/expired,
  // matching whichever kind of session they were last in.
  return sessionStorage.getItem("staff_token") && !localStorage.getItem("token")
    ? "/manager-login"
    : "/login";
}

export function getBusinessName() {
  const tenant = JSON.parse(localStorage.getItem("tenant") || "null");
  if (tenant) return tenant.business_name;
  const staffTenant = JSON.parse(sessionStorage.getItem("staff_tenant") || "null");
  if (staffTenant) return staffTenant.business_name;
  return "";
}

export function getOperatorIdentity() {
  // Display name + role for the Waiter/Kitchen/Manager Home header,
  // regardless of whether this is a PIN-based staff session or the
  // tenant's own login acting directly.
  const staff = JSON.parse(sessionStorage.getItem("staff") || "null");
  if (staff) return { name: staff.name, role: staff.role };

  const tenant = JSON.parse(localStorage.getItem("tenant") || "null");
  if (tenant) return { name: tenant.owner_name || tenant.business_name, role: "owner" };

  return null;
}

export function getOperatorLoginRedirect(station) {
  // Where to send someone whose session on a Waiter/Kitchen/Manager
  // Home screen is missing or expired — back to whichever kind of
  // login they were actually using.
  if (localStorage.getItem("token")) return "/login";
  const stationLogins = { waiter: "/waiter-login", kitchen: "/kitchen-login", manager: "/manager-login" };
  return stationLogins[station] || "/login";
}

export function setStaffSession(accessToken, staff, tenant) {
  sessionStorage.setItem("staff_token", accessToken);
  sessionStorage.setItem("staff", JSON.stringify(staff));
  sessionStorage.setItem("staff_tenant", JSON.stringify(tenant));
}

export function clearStaffSession() {
  sessionStorage.removeItem("staff_token");
  sessionStorage.removeItem("staff");
  sessionStorage.removeItem("staff_tenant");
}

export function clearOperatorSession() {
  // Used when navigating AWAY from operations (e.g. tenant clicking
  // back to their dashboard) without fully logging out — clears only
  // the staff-side session, never the tenant's own "token"/"tenant".
  clearStaffSession();
}
